// PostHog (COM-907) — observation layer only: autocapture feeds tap/scroll
// heatmaps, session replay shows real (90% mobile) visitor behaviour. Google
// Ads / GA4 conversion tracking lives in lib/attribution.ts and is untouched.
//
// The project API key is publishable by design (it can only ingest events),
// so it lives in code like the GA/AW ids in public/index.html.
import posthog from 'posthog-js';

const POSTHOG_KEY = 'phc_qEa2Erx4QDTt7xY7RAXAFw3FCbqcmrJhCseUECxZth7a';
const POSTHOG_HOST = 'https://us.i.posthog.com';

/**
 * The same production test the Google tags make in `public/index.html`, written
 * again here because that file is static and cannot import.
 *
 * `NODE_ENV` alone was not enough, and the gap was measured, not theorised: a
 * Vercel PREVIEW build is `NODE_ENV === 'production'`, so every preview
 * deployment wrote to the live PostHog project as if it were the real site. On
 * 2026-09-16 that produced an `application_submitted` with no row behind it in
 * `applications` — PostHog said one submission, our own database said none, and
 * the day's real total was zero. GA4 did not show it, because the Google tags
 * have checked the hostname since CMSY-72; PostHog had never been given the
 * same guard. Two systems that count the same thing must apply the same filter,
 * or the disagreement between them is just this bug.
 *
 * `navigator.webdriver` is the CMSY-101 half: PostHog's server-side `_is_bot()`
 * already drops headless traffic, which is why the tier-2 smoke never polluted
 * this project while it was inflating GA4. Relying on that is relying on a
 * vendor's filter to stay unchanged — a check that happens to be clean is not
 * the same as a check that cannot fire. Say it here too.
 *
 * Verifying PostHog events on a preview deployment therefore no longer works by
 * itself, which is the intended trade: measure on production, or run the funnel
 * locally against a build you started yourself.
 */
function isProductionVisitor(): boolean {
  try {
    const host = window.location.hostname;
    return (host === 'commissaryconnect.com' || host === 'www.commissaryconnect.com')
      && !navigator.webdriver;
  } catch {
    return false; // fails closed — no measurement beats wrong measurement
  }
}

export function initPostHog(): void {
  if (process.env.NODE_ENV !== 'production') return; // don't pollute with dev sessions
  if (!isProductionVisitor()) return;                // ...nor with preview deploys or robots
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: 'history_change', // SPA: count route changes as pageviews
    session_recording: { maskAllInputs: true },
    capture_exceptions: true, // JS errors in the wild become $exception events
  });
}

// Link an anonymous journey to a known applicant the moment we have their
// contact info (first-party identification of our own applicants). Safe to
// call repeatedly; degrades silently like everything else here.
/**
 * PostHog's anonymous per-browser id, stored on the application row so an
 * application can be joined to the apply_handoff click that preceded it
 * (CMSY-50). Never the distinct_id: identifyApplicant() sets that to the
 * applicant's email, and the marketing mirror is non-identifying by contract.
 */
export function deviceId(): string | null {
  try {
    if (!posthog.__loaded) return null;
    const id = posthog.get_property('$device_id');
    return typeof id === 'string' && id ? id : null;
  } catch {
    return null; // fails closed — a missing link, never a wrong one
  }
}

export function identifyApplicant(email: string, props: Record<string, string> = {}): void {
  try {
    if (posthog.__loaded && email) posthog.identify(email.toLowerCase(), props);
  } catch { /* degrade silently */ }
}

export { posthog };
